import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@dvms/ui';
import type { AppConfig } from '@dvms/shared-types';
import { loadConfig } from './config/loader';
import { ConfigProvider } from './config/context';
import { AuthProvider } from './auth';
import { DvmsStoreProvider } from './state/dvmsStore';
import { SysConfigCacheProvider } from './hooks/useSysConfigs';
import { router } from './router';
import { ROOT_ELEMENT_ID } from './constants';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000, // 30s — avoid refetching on remount / StrictMode double-render
            refetchOnWindowFocus: false,
        },
    },
});

function bootstrap() {
    try {
        const config: AppConfig = loadConfig();

        const rootElement = document.getElementById(ROOT_ELEMENT_ID);
        if (!rootElement) {
            throw new Error('Root element not found');
        }

        createRoot(rootElement).render(
            <StrictMode>
                <QueryClientProvider client={queryClient}>
                    <ThemeProvider>
                        <ConfigProvider config={config}>
                            <AuthProvider>
                                <SysConfigCacheProvider>
                                    <DvmsStoreProvider>
                                        <RouterProvider router={router} />
                                    </DvmsStoreProvider>
                                </SysConfigCacheProvider>
                            </AuthProvider>
                        </ConfigProvider>
                    </ThemeProvider>
                </QueryClientProvider>
            </StrictMode>,
        );
    } catch (error) {
        const rootElement = document.getElementById(ROOT_ELEMENT_ID);
        if (rootElement) {
            const container = document.createElement('div');
            container.style.cssText =
                'padding:2rem;color:#ef4444;font-family:system-ui;background:#1a1a2e;height:100vh';
            const h1 = document.createElement('h1');
            h1.textContent = 'Configuration Error';
            const p = document.createElement('p');
            p.textContent = error instanceof Error ? error.message : 'Failed to load application configuration.';
            const btn = document.createElement('button');
            btn.textContent = 'Retry';
            btn.style.cssText = 'margin-top:1rem;padding:0.5rem 1rem;cursor:pointer';
            btn.addEventListener('click', () => {
                location.reload();
            });
            container.append(h1, p, btn);
            rootElement.replaceChildren(container);
        }
    }
}

bootstrap();
